import React from 'react';
import PropTypes from 'prop-types';
import { LuMail, LuMapPin, LuPhone, LuUserRound } from 'react-icons/lu';
import Badge from '../../components/ui/Badge';
import useRegionText from './regionText';

/**
 * Workers with no active centre assignment in any project whose "Çalıştığı İl" is in
 * the open region / city (utils/regionScope). They have no project rows for
 * the shared worker table, so they get this list of their own.
 */
const ProjectlessWorkers = ({ workers, accentColor }) => {
  const { label } = useRegionText();
  if (workers.length === 0) return null;

  return (
    <div className="tw-space-y-2.5">
      <div>
        <h3 className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">
          {label('projectless.title')}
          <Badge tone="warning">{workers.length}</Badge>
        </h3>
        <p className="tw-mt-0.5 tw-text-xs tw-leading-snug tw-text-slate-500 dark:tw-text-slate-400">{label('projectless.text')}</p>
      </div>
      <ul className="tw-grid tw-gap-2 md:tw-grid-cols-2 xl:tw-grid-cols-3">
        {workers.map((worker) => {
          const fullName = `${worker.name} ${worker.surname}`.trim();
          return (
            <li
              key={worker.id}
              style={accentColor ? { '--accent': accentColor } : undefined}
              className={`tw-min-w-0 tw-rounded-2xl tw-border tw-border-l-4 tw-border-slate-200/80 tw-bg-white/70 tw-px-3.5 tw-py-2.5 dark:tw-border-white/10 dark:tw-bg-white/[0.03] ${accentColor ? 'tw-border-l-[color:var(--accent)] dark:tw-border-l-[color:var(--accent)]' : ''}`}
            >
              <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
                <LuUserRound aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400" />
                <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white" title={fullName}>{fullName || '-'}</span>
                <Badge tone="neutral">
                  <LuMapPin aria-hidden="true" className="tw-h-3 tw-w-3" />
                  {worker.workCity}
                </Badge>
              </div>
              {(worker.email || worker.phone) && (
                <div className="tw-mt-1.5 tw-flex tw-min-w-0 tw-flex-wrap tw-gap-x-4 tw-gap-y-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                  {worker.email && (
                    <a href={`mailto:${worker.email}`} className="tw-inline-flex tw-min-w-0 tw-items-center tw-gap-1 hover:tw-text-slate-800 dark:hover:tw-text-slate-200">
                      <LuMail aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
                      <span className="tw-truncate">{worker.email}</span>
                    </a>
                  )}
                  {worker.phone && (
                    <a href={`tel:${String(worker.phone).replace(/\s+/g, '')}`} className="tw-inline-flex tw-items-center tw-gap-1 hover:tw-text-slate-800 dark:hover:tw-text-slate-200">
                      <LuPhone aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
                      {worker.phone}
                    </a>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

ProjectlessWorkers.propTypes = {
  workers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    surname: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    workCity: PropTypes.string,
  })).isRequired,
  accentColor: PropTypes.string,
};

export default ProjectlessWorkers;
